import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, X, Zap, Loader2, FileText, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";

function QuickReplies({ onPick, onClose }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const load = () => api.get("/wa-inbox/quick-replies").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/wa-inbox/quick-replies", { title, text }); setTitle(""); setText(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="absolute bottom-full left-0 mb-2 w-[min(380px,90vw)] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-20" data-testid="inbox-quick-replies">
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between text-sm font-semibold text-[#111b21]">Saved replies <button onClick={onClose}><X className="w-4 h-4 text-[#54656f]" /></button></div>
      <div className="max-h-56 overflow-y-auto">
        {items.map((q) => (
          <div key={q.id} className="flex items-start gap-2 px-3 py-2 hover:bg-[#f5f6f6] cursor-pointer" onClick={() => onPick(q.text)} data-testid={`inbox-quick-${q.title}`}>
            <div className="flex-1 min-w-0"><div className="text-sm font-medium text-[#111b21]">{q.title}</div><div className="text-xs text-[#667781] line-clamp-2">{q.text}</div></div>
            <button onClick={(e) => { e.stopPropagation(); api.delete(`/wa-inbox/quick-replies/${q.id}`).then(load); }} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="px-3 py-4 text-xs text-[#667781] text-center">No saved replies yet — add one below.</div>}
      </div>
      <div className="p-2.5 border-t border-slate-100 space-y-1.5 bg-[#f7f8fa]">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Batch timing)" className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="inbox-quick-title-input" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Message text" className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="inbox-quick-text-input" />
        <button onClick={add} disabled={!title.trim() || !text.trim()} className="w-full bg-[#00a884] disabled:opacity-50 text-white text-sm rounded-md py-1.5 flex items-center justify-center gap-1" data-testid="inbox-quick-add-btn"><Plus className="w-4 h-4" /> Save reply</button>
      </div>
    </div>
  );
}

export function Composer({ conv, replyTo, clearReply, onSent, onTemplate, draft, setDraft }) {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [quick, setQuick] = useState(false);
  const fileRef = useRef(null);
  const taRef = useRef(null);
  useEffect(() => { taRef.current?.focus(); }, [conv.phone, replyTo]);

  if (!conv.window_open) {
    return (
      <div className="bg-[#f0f2f5] px-4 py-3 flex flex-col sm:flex-row items-center gap-3 justify-between" data-testid="inbox-window-closed">
        <span className="text-sm text-[#54656f] text-center sm:text-left">The 24-hour reply window is closed. WhatsApp only allows an approved template until the parent replies.</span>
        <button onClick={onTemplate} className="bg-[#00a884] hover:bg-[#008f72] text-white text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2 flex-shrink-0" data-testid="inbox-send-template-btn"><FileText className="w-4 h-4" /> Send template</button>
      </div>
    );
  }

  const send = async () => {
    if (busy || (!draft.trim() && !file)) return;
    setBusy(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", draft);
        if (replyTo?.wamid) fd.append("reply_to", replyTo.wamid);
        await api.post(`/wa-inbox/conversations/${conv.phone}/send-media`, fd);
      } else {
        await api.post(`/wa-inbox/conversations/${conv.phone}/send`, { text: draft, reply_to: replyTo?.wamid || null });
      }
      setDraft(""); setFile(null); clearReply(); onSent();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="bg-[#f0f2f5] flex-shrink-0">
      {replyTo && (
        <div className="mx-3 mt-2 bg-white rounded-lg border-l-4 border-[#06cf9c] px-3 py-2 flex items-start gap-2" data-testid="inbox-reply-preview">
          <div className="flex-1 min-w-0 text-xs"><div className="font-semibold text-[#008069]">Replying to {replyTo.direction === "out" ? "yourself" : "parent"}</div><div className="text-[#667781] truncate">{replyTo.text || replyTo.caption || replyTo.type}</div></div>
          <button onClick={clearReply}><X className="w-4 h-4 text-[#54656f]" /></button>
        </div>
      )}
      {file && (
        <div className="mx-3 mt-2 bg-white rounded-lg px-3 py-2 flex items-center gap-2 text-sm" data-testid="inbox-file-preview">
          <Paperclip className="w-4 h-4 text-[#54656f]" /><span className="flex-1 truncate">{file.name}</span>
          <span className="text-xs text-[#667781]">{(file.size / 1024).toFixed(0)} KB</span>
          <button onClick={() => setFile(null)}><X className="w-4 h-4 text-[#54656f]" /></button>
        </div>
      )}
      <div className="px-3 py-2.5 flex items-end gap-2 relative">
        {quick && <QuickReplies onClose={() => setQuick(false)} onPick={(t) => { setDraft(t); setQuick(false); }} />}
        <button onClick={() => setQuick((q) => !q)} className="w-10 h-10 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f] flex-shrink-0" title="Saved replies" data-testid="inbox-quick-btn"><Zap className="w-5 h-5" /></button>
        <button onClick={() => fileRef.current?.click()} className="w-10 h-10 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f] flex-shrink-0" title="Attach" data-testid="inbox-attach-btn"><Paperclip className="w-5 h-5" /></button>
        <input ref={fileRef} type="file" className="hidden" accept="image/jpeg,image/png,application/pdf,video/mp4,audio/*,.doc,.docx,.xls,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="inbox-file-input" />
        <button onClick={onTemplate} className="w-10 h-10 rounded-full hover:bg-black/5 flex items-center justify-center text-[#54656f] flex-shrink-0" title="Send template" data-testid="inbox-template-btn"><FileText className="w-5 h-5" /></button>
        <textarea ref={taRef} value={draft} onChange={(e) => setDraft(e.target.value)} rows={1}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={file ? "Add a caption" : "Type a message"} data-testid="inbox-message-input"
          className="flex-1 resize-none bg-white rounded-lg px-3.5 py-2.5 text-[15px] outline-none max-h-32 min-h-[42px]" style={{ fieldSizing: "content" }} />
        <button onClick={send} disabled={busy || (!draft.trim() && !file)} className="w-11 h-11 rounded-full bg-[#00a884] hover:bg-[#008f72] disabled:opacity-50 text-white flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform" data-testid="inbox-send-btn">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
